import React from "react";
import { useCountry } from "../../contexts/CountryContext";
import { canJumpToStep } from "./wizard-steps";

/**
 * The step bar above the eligibility assessment wizard: the same control as the US
 * assessment's AssessmentStepper (OPTRA US/grant-eligibility/src/components/assessment/
 * AssessmentStepper.tsx), so the two assessments read alike. One numbered square per step with
 * its title and a one-line description; completed steps show a check, the current step is
 * filled in ink, and a step is a button only when the person may jump to it (completed steps,
 * the current one, and the first step after the furthest completed). Below 768px each step is a
 * narrow column (number above a two-line title) and the row scrolls sideways inside its frame.
 * Styles: `.cw-stepper*` in src/styles/theme-sheet-product.css (section 13b).
 *
 * `data-testid="eligibility-stepper"` / `stepper-pill` / `step-N` are kept for the tests.
 */

const sgSteps7 = [
 { id: 1, title: "Company Basics", description: "Basic company information" },
 { id: 2, title: "Business Metrics", description: "Financial and operational metrics" },
 { id: 3, title: "Location & Ops", description: "Operational presence and location" },
 { id: 4, title: "Grant History", description: "Previous grant applications" },
 { id: 5, title: "Funding", description: "Funding strategy and fit" },
 { id: 6, title: "Project", description: "Project details and budget" },
 { id: 7, title: "Compliance", description: "Documents and declarations" }
];

const sgSteps8 = [
 ...sgSteps7,
 { id: 8, title: "Review", description: "Final review and submission" }
];

/** Singapore flow with supporting-documents step before review */
const sgSteps9 = [
 ...sgSteps7,
 { id: 8, title: "Supporting docs", description: "Optional file uploads" },
 { id: 9, title: "Review", description: "Final review and submission" }
];

const usSteps = [
 { id: 1, title: "Company Profile", description: "US company information" },
 { id: 2, title: "Business Metrics", description: "Financial & innovation metrics" },
 { id: 3, title: "Team & Qualifications", description: "Team and prior funding" },
 { id: 4, title: "Project & Commercialization", description: "Project and market validation" }
];

export default function StepperProgress({ currentStep, completedSteps = [], totalSteps, onStepClick }) {
  const { country } = useCountry();
  const isUS = country === 'US';
  const steps = isUS ? usSteps : totalSteps === 9 ? sgSteps9 : totalSteps === 8 ? sgSteps8 : sgSteps7;
  const displaySteps = totalSteps ? steps.slice(0, totalSteps) : steps;

  const handleStepClick = (stepId) => {
    if (!onStepClick) return;
    if (canJumpToStep(stepId, currentStep, completedSteps)) onStepClick(stepId);
  };

  return (
    <nav
      aria-label="Assessment steps"
      className="cw-stepper"
      data-testid="eligibility-stepper"
    >
      <ol className="cw-stepper__list" data-testid="stepper-pill">
        {displaySteps.map((step, index) => {
          const done = completedSteps.includes(step.id);
          const current = step.id === currentStep;
          const clickable = canJumpToStep(step.id, currentStep, completedSteps);
          const glyph = done && !current ? "✓" : String(step.id);
          const state = current ? "current" : done ? "done" : "todo";
          const label = `Step ${step.id}: ${step.title}${done ? " (completed)" : current ? " (current)" : ""}`;
          return (
            <li key={step.id} className="cw-stepper__item" data-testid={`step-${step.id}`}>
              <button
                type="button"
                onClick={() => handleStepClick(step.id)}
                disabled={!clickable}
                aria-current={current ? "step" : undefined}
                aria-label={label}
                className="cw-stepper__button"
                data-state={state}
              >
                <span aria-hidden className="cw-stepper__glyph">{glyph}</span>
                <span className="cw-stepper__text">
                  <span className="cw-stepper__title">{step.title}</span>
                  <span className="cw-stepper__desc">{step.description}</span>
                </span>
              </button>
              {index < displaySteps.length - 1 && (
                <span aria-hidden className="cw-stepper__link" data-done={done ? "true" : "false"} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
